import { useEffect } from "react";
import { NextPage } from "next";
import dynamic from "next/dynamic";
import { parse, stringify } from "qs";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getMenuItems, { GetMenuItemsReturn } from "api/requests/getMenuItems";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { MenuProvider } from "context/MenuContext";
import { Dispensary } from "custom-types/Dispensary";
import { COMPLIANCE_RULESET_LOAD } from "redux/reducers/complianceRules";
import { getDomainCountryCode } from "redux/selectors/config";
import logError from "utils/logError";
import { getMenuTypeCookie, includesDualMenuTag } from "utils/menuTypeUtils";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";
import { trackUserHistory } from "utils/userHistory";
import { validateDispensary } from "utils/validateDispensary";

import DispensaryBreadcrumbs from "components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import MenuContainer from "components/Dispensary/MenuContainer";
import MenuNotAvailable from "components/Dispensary/MenuNotAvailable";

const NearbyStores = dynamic(
  () => import("components/Dispensary/NearbyStores"),
);

type Props = {
  dispensary: Dispensary;
  menuData: GetMenuItemsReturn;
};

const MenuPage: NextPage<Props> = ({ dispensary, menuData }) => {
  const { id, featureTier, slug, hasMenuItems } = dispensary;
  const isInfoTier = featureTier === "info";

  useEffect(() => {
    trackUserHistory({
      resourceId: id,
      slug,
      type: "dispensary",
    });
  }, [id]);

  useTrackDispensaryPageViewSplitEvent({ page: "menu", tier: featureTier });

  return (
    <MenuProvider initialData={menuData}>
      <DispensaryHead dispensary={dispensary} pageType="menu" noIndex={true} />
      <DispensaryHeaderSlim dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.menu}
        dispensary={dispensary}
      />
      {!isInfoTier && hasMenuItems ? (
        <MenuContainer />
      ) : (
        <>
          <MenuNotAvailable className="container my-section" />
          {isInfoTier && (
            <NearbyStores className="container mt-xl md:mt-[40px] mb-lg" />
          )}
        </>
      )}

      <DispensaryBreadcrumbs dispensary={dispensary} />
    </MenuProvider>
  );
};

MenuPage.getInitialProps = async (context) => {
  const { req, res, query, store } = context;
  const { slug, menu_type, ...remainingQuery } = query;
  const countryCode = getDomainCountryCode(store.getState());

  try {
    const dispensary = await getDispensary(slug as string);

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });

    if (redirect) {
      return;
    }

    const userMedRecPreference = menu_type || getMenuTypeCookie(req);

    const isDualLicenseDispensary = includesDualMenuTag(dispensary?.tags || []);

    store.dispatch({
      dispensary,
      type: "dispensary/load",
    });

    const getMenuItemsCall = getMenuItems(slug as string, {
      ...parse(stringify(remainingQuery)),
      enableNewFilters: true,
      menu_type: isDualLicenseDispensary ? userMedRecPreference : undefined,
    });

    const [filteredMenu, complianceRuleset] = await Promise.all([
      getMenuItemsCall,
      getRegionComplianceRules(dispensary?.state, dispensary?.country),
    ]);

    // The getMenuItems call only returns totalItems of that specific query, not the
    // total total items. To avoid showing "menu not available" instead of "no results"
    // we retrieve menu items without any filters

    const menuFallback =
      Object.keys(remainingQuery).length && filteredMenu.totalItems === 0
        ? await getMenuItems(slug as string, {
            menu_type: isDualLicenseDispensary
              ? userMedRecPreference
              : undefined,
            perPage: 0,
          })
        : filteredMenu;

    const menuData = {
      ...filteredMenu,
      totalItems: menuFallback.totalItems,
    };

    if (complianceRuleset) {
      store.dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }

    return {
      dispensary,
      menuData,
    };
  } catch (err) {
    logError(err.message || "Unknown error", {
      functionName: "/dispensary-info/[slug]/menu getInitialProps",
    });

    return err;
  }
};

export default MenuPage;
